/* ==========================================================================
   ELIYEEN — global.js
   Cart AJAX + drawer, wishlist (localStorage), quick view, carousels,
   slideshow, header behaviour, scroll reveals (IO + optional GSAP),
   accordions, overlays. No dependencies; GSAP enhances when present.
   ========================================================================== */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, settings: {}, strings: {} };

  /* --- Utilities ---------------------------------------------------------- */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function debounce(fn, wait = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  /* Shopify money formatting using the store's money_format */
  function formatMoney(cents, format = theme.moneyFormat || '£{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function delimit(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    let value = '';
    switch (format.match(placeholderRegex)[1]) {
      case 'amount': value = delimit(cents, 2); break;
      case 'amount_no_decimals': value = delimit(cents, 0); break;
      case 'amount_with_comma_separator': value = delimit(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(cents, 0, '.', ','); break;
      default: value = delimit(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* Append a width param to a CDN image URL whether or not it has a query string */
  function withWidth(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  window.eliyeen = window.eliyeen || {};
  window.eliyeen.formatMoney = formatMoney;

  /* Focus trap for drawers/modals */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let trapHandler = null;

  function trapFocus(container) {
    releaseFocus();
    const nodes = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler);
    first.focus({ preventScroll: true });
  }

  function releaseFocus() {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
  }

  /* --- Overlay / drawer / modal manager ------------------------------------ */

  const overlay = $('[data-page-overlay]');
  let openPanel = null;
  let lastFocused = null;

  function openDrawer(panel) {
    if (!panel) return;
    if (openPanel && openPanel !== panel) closeDrawer(true);
    lastFocused = document.activeElement;
    openPanel = panel;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-active');
    document.body.classList.add('overflow-hidden');
    trapFocus(panel);
    document.dispatchEvent(new CustomEvent('eliyeen:panel:open', { detail: { panel } }));
  }

  function closeDrawer(keepOverlay = false) {
    if (!openPanel) return;
    openPanel.classList.remove('is-open');
    openPanel.setAttribute('aria-hidden', 'true');
    openPanel = null;
    releaseFocus();
    if (!keepOverlay) {
      if (overlay) overlay.classList.remove('is-active');
      document.body.classList.remove('overflow-hidden');
      if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(document.getElementById(opener.getAttribute('data-drawer-open')));
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-page-overlay]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  window.eliyeen.openDrawer = openDrawer;
  window.eliyeen.closeDrawer = closeDrawer;

  /* --- Cart ------------------------------------------------------------------- */

  const Cart = {
    sectionsToUpdate() {
      return ['cart-drawer'];
    },

    async add(body) {
      body.sections = this.sectionsToUpdate().join(',');
      body.sections_url = window.location.pathname;
      const res = await fetch(theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw data;
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('eliyeen:cart:added', { detail: data }));
      await this.refreshCount();
      return data;
    },

    async change(line, quantity) {
      const res = await fetch(theme.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionsToUpdate().join(','), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw data;
      this.renderSections(data.sections);
      document.dispatchEvent(new CustomEvent('eliyeen:cart:changed', { detail: data }));
      await this.refreshCount(data.item_count);
      return data;
    },

    renderSections(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        const drawer = $('#CartDrawer');
        if (drawer) {
          const dom = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const fresh = dom.querySelector('#CartDrawer');
          if (fresh) {
            drawer.innerHTML = fresh.innerHTML;
            if (openPanel === drawer) trapFocus(drawer);
          }
        }
      }
    },

    async refreshCount(known) {
      let count = known;
      if (typeof count !== 'number') {
        try {
          const res = await fetch(theme.routes.cart + '.js', { headers: { Accept: 'application/json' } });
          count = (await res.json()).item_count;
        } catch (err) {
          return;
        }
      }
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.toggleAttribute('hidden', count === 0);
      });
    },

    open() {
      const drawer = $('#CartDrawer');
      if (drawer && theme.settings.cartType === 'drawer') openDrawer(drawer);
      else window.location.href = theme.routes.cart;
    }
  };

  window.eliyeen.cart = Cart;

  /* Quantity selector (used in PDP, cart, drawer, quick view) */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrap = btn.closest('.qty');
    const input = wrap && wrap.querySelector('.qty__input');
    if (!input) return;
    const step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    const min = parseInt(input.min || 1, 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Cart drawer + cart page line quantity/remove (event delegation survives re-render) */
  async function handleLineChange(lineEl, quantity) {
    const line = parseInt(lineEl.getAttribute('data-line'), 10);
    lineEl.classList.add('is-loading');
    try {
      const data = await Cart.change(line, quantity);
      const cartRoot = $('[data-cart-root]');
      if (cartRoot) await refreshCartSection(cartRoot);
    } catch (err) {
      lineEl.classList.remove('is-loading');
      window.alert((err && err.description) || theme.strings.addError);
    }
  }

  /* Re-render the main cart section in place (cart page) */
  async function refreshCartSection(cartRoot) {
    const sectionId = cartRoot.getAttribute('data-section-id');
    const res = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
    const html = await res.text();
    const dom = new DOMParser().parseFromString(html, 'text/html');
    const fresh = dom.querySelector('[data-cart-root]');
    if (fresh) cartRoot.innerHTML = fresh.innerHTML;
  }

  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const lineEl = removeBtn.closest('[data-line]');
      if (lineEl) handleLineChange(lineEl, 0);
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) {
      const lineEl = input.closest('[data-line]');
      if (lineEl) handleLineChange(lineEl, parseInt(input.value, 10) || 0);
    }
  });

  /* Product forms — AJAX add to cart */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('[data-atc-button]');
    const idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return;
    const qtyInput = form.querySelector('[name="quantity"]');
    const originalText = submitBtn ? submitBtn.querySelector('[data-atc-text]') : null;

    if (submitBtn) {
      submitBtn.setAttribute('disabled', '');
      submitBtn.classList.add('is-adding');
      if (originalText) originalText.textContent = theme.strings.adding;
    }

    const body = { items: [{ id: parseInt(idInput.value, 10), quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1 }] };

    /* Include cart note / properties if present */
    $$('input[name^="properties"], textarea[name^="properties"]', form).forEach((prop) => {
      if (!prop.value) return;
      const key = prop.name.match(/properties\[(.*?)\]/);
      if (key) {
        body.items[0].properties = body.items[0].properties || {};
        body.items[0].properties[key[1]] = prop.value;
      }
    });

    try {
      await Cart.add(body);
      if (submitBtn) {
        submitBtn.classList.remove('is-adding');
        submitBtn.classList.add('is-added');
        if (originalText) originalText.textContent = theme.strings.added;
        setTimeout(() => {
          submitBtn.removeAttribute('disabled');
          submitBtn.classList.remove('is-added');
          if (originalText) originalText.textContent = theme.strings.addToCart;
        }, 1800);
      }
      const quickView = $('#QuickViewModal');
      if (quickView && quickView.classList.contains('is-open')) closeModal(quickView);
      Cart.open();
    } catch (err) {
      if (submitBtn) {
        submitBtn.removeAttribute('disabled');
        submitBtn.classList.remove('is-adding');
        if (originalText) originalText.textContent = theme.strings.addToCart;
      }
      window.alert((err && err.description) || theme.strings.addError);
    }
  });

  /* --- Modals -------------------------------------------------------------------- */

  function openModal(modal) {
    if (!modal) return;
    lastFocused = document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    trapFocus(modal);
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    releaseFocus();
    if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const modalOpener = e.target.closest('[data-modal-open]');
    if (modalOpener) {
      e.preventDefault();
      openModal(document.getElementById(modalOpener.getAttribute('data-modal-open')));
      return;
    }
    if (e.target.closest('[data-modal-close]') || e.target.classList.contains('modal__backdrop')) {
      closeModal(e.target.closest('.modal'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const open = $('.modal.is-open');
      if (open) closeModal(open);
    }
  });

  window.eliyeen.openModal = openModal;
  window.eliyeen.closeModal = closeModal;

  /* --- Wishlist (localStorage, no app) --------------------------------------------- */

  const WISHLIST_KEY = 'eliyeen:wishlist';

  const Wishlist = {
    all() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (err) {
        return [];
      }
    },
    has(handle) {
      return this.all().includes(handle);
    },
    toggle(handle) {
      let items = this.all();
      const active = items.includes(handle);
      items = active ? items.filter((h) => h !== handle) : [...items, handle];
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      this.syncHearts();
      document.dispatchEvent(new CustomEvent('eliyeen:wishlist:changed', { detail: { items } }));
      return !active;
    },
    clear() {
      localStorage.setItem(WISHLIST_KEY, '[]');
      this.syncHearts();
      document.dispatchEvent(new CustomEvent('eliyeen:wishlist:changed', { detail: { items: [] } }));
    },
    syncHearts() {
      const items = this.all();
      $$('[data-wishlist-toggle]').forEach((btn) => {
        const active = items.includes(btn.getAttribute('data-handle'));
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.setAttribute('aria-label', active ? theme.strings.wishlistRemove : theme.strings.wishlistAdd);
      });
      $$('[data-wishlist-count]').forEach((el) => {
        el.textContent = items.length;
        el.toggleAttribute('hidden', items.length === 0);
      });
    }
  };

  window.eliyeen.wishlist = Wishlist;

  document.addEventListener('click', (e) => {
    const heart = e.target.closest('[data-wishlist-toggle]');
    if (!heart) return;
    e.preventDefault();
    const added = Wishlist.toggle(heart.getAttribute('data-handle'));
    if (added) {
      heart.classList.remove('is-pulsing');
      void heart.offsetWidth; /* restart animation */
      heart.classList.add('is-pulsing');
    }
  });

  /* --- Quick view -------------------------------------------------------------------- */

  async function openQuickView(handle) {
    const modal = $('#QuickViewModal');
    if (!modal) return;
    const content = $('[data-quick-view-content]', modal);
    content.innerHTML = '<div class="quick-view__loading"><div class="spinner"></div></div>';
    openModal(modal);
    try {
      const res = await fetch(`${theme.routes.root === '/' ? '' : theme.routes.root}/products/${handle}.js`);
      if (!res.ok) throw new Error('not found');
      const product = await res.json();
      content.innerHTML = buildQuickViewHTML(product);
      initQuickViewVariants(content, product);
      trapFocus(modal);
    } catch (err) {
      window.location.href = `${theme.routes.root === '/' ? '' : theme.routes.root}/products/${handle}`;
    }
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function buildQuickViewHTML(product) {
    const images = (product.images || []).slice(0, 5);
    const imageHTML = images.length
      ? `<div class="quick-view__gallery" data-qv-gallery>
          <div class="quick-view__main"><img src="${withWidth(images[0], 900)}" data-qv-main alt="${escapeHtml(product.title)}" width="900" height="1125" loading="eager"></div>
          ${images.length > 1 ? `<div class="quick-view__thumbs">${images.map((src, i) => `<button type="button" class="quick-view__thumb${i === 0 ? ' is-active' : ''}" data-qv-thumb="${withWidth(src, 900)}"><img src="${withWidth(src, 160)}" alt="" width="160" height="200" loading="lazy"></button>`).join('')}</div>` : ''}
        </div>`
      : '<div class="quick-view__gallery quick-view__gallery--empty"></div>';

    const optionsHTML = (product.options || [])
      .map((option, index) => {
        if (product.variants.length === 1 && product.variants[0].title === 'Default Title') return '';
        const values = [...new Set(product.variants.map((v) => v.options[index]))];
        return `
          <fieldset class="quick-view__option" data-qv-option data-index="${index}">
            <legend class="field__label">${escapeHtml(option.name || option)}</legend>
            <div class="quick-view__values">
              ${values
                .map(
                  (value, vi) => `
                <input class="swatch-input" type="radio" id="qv-${index}-${vi}" name="qv-option-${index}" value="${escapeHtml(value)}" ${vi === 0 ? 'checked' : ''}>
                <label class="swatch" for="qv-${index}-${vi}">${escapeHtml(value)}</label>`
                )
                .join('')}
            </div>
          </fieldset>`;
      })
      .join('');

    return `
      <div class="quick-view__grid">
        ${imageHTML}
        <div class="quick-view__details">
          <p class="eyebrow eyebrow--plain">${escapeHtml(product.vendor || '')}</p>
          <h2 class="quick-view__title h3">${escapeHtml(product.title)}</h2>
          <div class="quick-view__price price" data-qv-price></div>
          <form data-product-form data-qv-form>
            <input type="hidden" name="id" value="">
            ${optionsHTML}
            <div class="quick-view__actions">
              <div class="qty">
                <button type="button" class="qty__btn" data-qty-change="down" aria-label="−">−</button>
                <input class="qty__input" type="number" name="quantity" value="1" min="1" inputmode="numeric">
                <button type="button" class="qty__btn" data-qty-change="up" aria-label="+">+</button>
              </div>
              <button type="submit" class="btn btn--primary quick-view__atc" data-atc-button>
                <span data-atc-text>${theme.strings.addToCart}</span>
              </button>
            </div>
          </form>
          <a class="link quick-view__full-link" href="${product.url}">${theme.strings.viewDetails || 'View full details'}</a>
        </div>
      </div>`;
  }

  function initQuickViewVariants(content, product) {
    const form = $('[data-qv-form]', content);
    const idInput = form.querySelector('[name="id"]');
    const priceEl = $('[data-qv-price]', content);
    const atcBtn = $('[data-atc-button]', form);
    const atcText = $('[data-atc-text]', form);
    const mainImg = $('[data-qv-main]', content);

    function selectedOptions() {
      return $$('[data-qv-option]', form).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function matchVariant() {
      const opts = selectedOptions();
      if (!opts.length) return product.variants[0];
      return product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }

    function render() {
      const variant = matchVariant();
      if (!variant) {
        idInput.value = '';
        atcBtn.setAttribute('disabled', '');
        atcText.textContent = theme.strings.soldOut;
        return;
      }
      idInput.value = variant.id;
      const compare = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceEl.classList.toggle('price--on-sale', !!compare);
      priceEl.innerHTML = `
        <span class="price__current">${formatMoney(variant.price)}</span>
        ${compare ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}`;
      if (variant.available) {
        atcBtn.removeAttribute('disabled');
        atcText.textContent = theme.strings.addToCart;
      } else {
        atcBtn.setAttribute('disabled', '');
        atcText.textContent = theme.strings.soldOut;
      }
      if (variant.featured_image && mainImg) {
        mainImg.src = withWidth(variant.featured_image.src, 900);
      }
    }

    form.addEventListener('change', render);
    content.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-qv-thumb]');
      if (thumb && mainImg) {
        mainImg.classList.add('is-fading');
        setTimeout(() => {
          mainImg.src = thumb.getAttribute('data-qv-thumb');
          mainImg.classList.remove('is-fading');
        }, 180);
        $$('.quick-view__thumb', content).forEach((t) => t.classList.toggle('is-active', t === thumb));
      }
    });
    render();
  }

  document.addEventListener('click', (e) => {
    const qv = e.target.closest('[data-quick-view]');
    if (qv) {
      e.preventDefault();
      openQuickView(qv.getAttribute('data-handle'));
      return;
    }
    /* Quick add: single-variant products add straight to cart; otherwise open quick view */
    const qa = e.target.closest('[data-quick-add]');
    if (qa) {
      e.preventDefault();
      const variantId = qa.getAttribute('data-variant-id');
      if (variantId) {
        qa.classList.add('is-adding');
        Cart.add({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
          .then(() => {
            qa.classList.remove('is-adding');
            Cart.open();
          })
          .catch((err) => {
            qa.classList.remove('is-adding');
            window.alert((err && err.description) || theme.strings.addError);
          });
      } else {
        openQuickView(qa.getAttribute('data-handle'));
      }
    }
  });

  /* --- Mini product card renderer (wishlist page, recently viewed) ------------------- */

  window.eliyeen.renderMiniCard = function (product) {
    const image = product.images && product.images[0] ? withWidth(product.images[0], 600) : '';
    const compare = product.compare_at_price && product.compare_at_price > product.price;
    const soldOut = !product.available;
    return `
      <div class="product-card mini-card${soldOut ? ' product-card--soldout' : ''}" data-handle="${product.handle}">
        <div class="product-card__media">
          <a href="${product.url}" class="product-card__link" tabindex="-1" aria-hidden="true">
            ${image ? `<img class="product-card__img" src="${image}" alt="${escapeHtml(product.title)}" width="600" height="750" loading="lazy">` : ''}
          </a>
          ${soldOut ? `<span class="badge badge--soldout product-card__badge">${theme.strings.soldOut}</span>` : ''}
          <button type="button" class="product-card__heart icon-btn is-active" data-wishlist-toggle data-handle="${product.handle}" aria-pressed="true" aria-label="${theme.strings.wishlistRemove}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path class="heart-path" d="M12 20.5s-7.5-4.7-10-9.5C.6 8.2 2.4 4.5 6 4.5c2.3 0 3.9 1.3 6 3.7 2.1-2.4 3.7-3.7 6-3.7 3.6 0 5.4 3.7 4 6.5-2.5 4.8-10 9.5-10 9.5z"/></svg>
          </button>
        </div>
        <div class="product-card__info">
          <h3 class="product-card__title"><a href="${product.url}">${escapeHtml(product.title)}</a></h3>
          <div class="price${compare ? ' price--on-sale' : ''}">
            <span class="price__current">${formatMoney(product.price)}</span>
            ${compare ? `<s class="price__compare">${formatMoney(product.compare_at_price)}</s>` : ''}
          </div>
        </div>
      </div>`;
  };

  /* --- Recently viewed ------------------------------------------------------------------ */

  const RECENT_KEY = 'eliyeen:recently-viewed';

  window.eliyeen.recentlyViewed = {
    all() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
      } catch (err) {
        return [];
      }
    },
    push(handle) {
      let items = this.all().filter((h) => h !== handle);
      items.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, 12)));
    }
  };

  /* --- Carousels (scroll-snap + arrows) ---------------------------------------------------- */

  function initCarousels(root = document) {
    $$('[data-carousel]', root).forEach((wrap) => {
      if (wrap.dataset.carouselInit) return;
      wrap.dataset.carouselInit = 'true';
      const track = $('[data-carousel-track]', wrap);
      const prev = $('[data-carousel-prev]', wrap);
      const next = $('[data-carousel-next]', wrap);
      if (!track) return;

      function page() {
        const item = track.firstElementChild;
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || 0);
        return item.getBoundingClientRect().width + gap;
      }

      function updateButtons() {
        if (!prev || !next) return;
        const max = track.scrollWidth - track.clientWidth - 4;
        prev.toggleAttribute('disabled', track.scrollLeft <= 4);
        next.toggleAttribute('disabled', track.scrollLeft >= max);
      }

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -page(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: page(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(updateButtons, 80), { passive: true });
      window.addEventListener('resize', debounce(updateButtons, 150));
      updateButtons();
    });
  }

  /* --- Hero slideshow -------------------------------------------------------------------------- */

  function initSlideshows(root = document) {
    $$('[data-slideshow]', root).forEach((slideshow) => {
      if (slideshow.dataset.slideshowInit) return;
      slideshow.dataset.slideshowInit = 'true';
      const slides = $$('[data-slide]', slideshow);
      if (slides.length <= 1) {
        if (slides[0]) slides[0].classList.add('is-active');
        return;
      }
      const dots = $$('[data-slide-dot]', slideshow);
      const autoplay = parseInt(slideshow.getAttribute('data-autoplay'), 10) || 0;
      let index = 0;
      let timer = null;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function goTo(next) {
        slides[index].classList.remove('is-active');
        if (dots[index]) dots[index].classList.remove('is-active');
        index = (next + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        if (dots[index]) dots[index].classList.add('is-active');
      }

      function play() {
        if (!autoplay || reduced) return;
        stop();
        timer = setInterval(() => goTo(index + 1), autoplay * 1000);
      }

      function stop() {
        if (timer) clearInterval(timer);
      }

      slideshow.addEventListener('pointerenter', stop);
      slideshow.addEventListener('pointerleave', play);
      slideshow.addEventListener('focusin', stop);
      slideshow.addEventListener('focusout', play);

      dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          goTo(i);
          play();
        })
      );

      const prevBtn = $('[data-slide-prev]', slideshow);
      const nextBtn = $('[data-slide-next]', slideshow);
      if (prevBtn) prevBtn.addEventListener('click', () => { goTo(index - 1); play(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { goTo(index + 1); play(); });

      /* Touch swipe */
      let startX = null;
      slideshow.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      slideshow.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 48) goTo(index + (dx < 0 ? 1 : -1));
        startX = null;
        play();
      }, { passive: true });

      slides[0].classList.add('is-active');
      if (dots[0]) dots[0].classList.add('is-active');
      play();
    });
  }

  /* --- Accordions (animated details) ------------------------------------------------------------ */

  function initAccordions(root = document) {
    $$('details[data-accordion]', root).forEach((details) => {
      if (details.dataset.accordionInit) return;
      details.dataset.accordionInit = 'true';
      const summary = $('summary', details);
      const body = $('[data-accordion-body]', details);
      if (!summary || !body) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced) {
          details.open = !details.open;
          return;
        }
        if (details.open) {
          const h = body.offsetHeight;
          body.style.height = h + 'px';
          requestAnimationFrame(() => {
            body.style.height = '0px';
            body.style.opacity = '0';
          });
          body.addEventListener(
            'transitionend',
            () => {
              details.open = false;
              body.style.height = '';
              body.style.opacity = '';
            },
            { once: true }
          );
        } else {
          details.open = true;
          const h = body.offsetHeight;
          body.style.height = '0px';
          body.style.opacity = '0';
          requestAnimationFrame(() => {
            body.style.height = h + 'px';
            body.style.opacity = '1';
          });
          body.addEventListener(
            'transitionend',
            () => {
              body.style.height = '';
              body.style.opacity = '';
            },
            { once: true }
          );
        }
      });
    });
  }

  /* --- Scroll reveal (IntersectionObserver) ------------------------------------------------------- */

  function initReveals(root = document) {
    if (!theme.settings.animations) return;
    const targets = $$('[data-reveal]', root).filter((el) => !el.dataset.revealInit);
    if (!targets.length) return;

    /* Stagger children inside containers marked data-reveal-stagger */
    $$('[data-reveal-stagger]', root).forEach((container) => {
      $$('[data-reveal]', container).forEach((child, i) => {
        child.style.setProperty('--reveal-delay', `${Math.min(i * 0.08, 0.64)}s`);
      });
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach((el) => {
      el.dataset.revealInit = 'true';
      io.observe(el);
    });
  }

  /* --- GSAP enhancements (hero entrance + parallax) -------------------------------------------------
     GSAP is used only where CSS can't: scrubbed scroll parallax and
     coordinated multi-element hero timelines. Everything else is CSS/IO. */

  let gsapInitDone = false;

  function initGsap() {
    if (gsapInitDone) return;
    if (!theme.settings.animations || !theme.settings.gsap) return;
    if (typeof window.gsap === 'undefined') return;
    gsapInitDone = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const gsap = window.gsap;
    if (typeof window.ScrollTrigger !== 'undefined') gsap.registerPlugin(window.ScrollTrigger);

    /* Hero entrance: word-by-word heading rise + staggered supporting items */
    $$('[data-gsap-hero]').forEach((hero) => {
      const heading = $('.hero__heading', hero);
      if (heading && !heading.dataset.split) {
        heading.dataset.split = 'true';
        heading.innerHTML = heading.textContent
          .trim()
          .split(/\s+/)
          .map((w) => `<span class="hero__word"><span>${w}</span></span>`)
          .join(' ');
      }
      const words = heading ? $$('.hero__word > span', heading) : [];
      const items = $$('[data-gsap-hero-item]', hero).filter((el) => el !== heading);

      const tl = gsap.timeline({ delay: 0.25, defaults: { ease: 'power3.out' } });
      if (words.length) {
        tl.fromTo(words, { yPercent: 115 }, { yPercent: 0, duration: 0.9, stagger: 0.07 });
      }
      if (items.length) {
        tl.fromTo(items, { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.12 }, words.length ? '-=0.55' : 0);
      }
    });

    /* Jewellery icons draw themselves in as they scroll into view */
    if (typeof window.ScrollTrigger !== 'undefined') {
      $$('[data-icon-draw]').forEach((wrap) => {
        const shapes = $$('path, circle, rect, ellipse, line, polyline', wrap).filter((s) => {
          if (typeof s.getTotalLength !== 'function') return false;
          try { return s.getTotalLength() > 0; } catch (err) { return false; }
        });
        if (!shapes.length) return;
        shapes.forEach((s) => {
          const len = s.getTotalLength();
          s.style.strokeDasharray = len;
          s.style.strokeDashoffset = len;
        });
        gsap.to(shapes, {
          strokeDashoffset: 0,
          duration: 1.5,
          ease: 'power2.inOut',
          stagger: 0.1,
          scrollTrigger: { trigger: wrap, start: 'top 88%', once: true }
        });
      });
    }

    /* Gentle parallax on marked images */
    if (typeof window.ScrollTrigger !== 'undefined') {
      $$('[data-parallax]').forEach((el) => {
        const strength = parseFloat(el.getAttribute('data-parallax')) || 8;
        gsap.fromTo(
          el,
          { yPercent: -strength / 2 },
          {
            yPercent: strength / 2,
            ease: 'none',
            scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
          }
        );
      });
    }
  }

  /* --- Header behaviour ---------------------------------------------------------------------------- */

  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    let lastScroll = window.scrollY;

    /* The header's sticky wrapper creates a stacking context (z-index 50),
       which would trap the menu/search drawers BELOW the page overlay (z 60).
       Teleport them to <body> so they layer correctly above the overlay. */
    ['MenuDrawer', 'SearchDrawer'].forEach((id) => {
      const drawer = document.getElementById(id);
      if (drawer && drawer.parentElement !== document.body) {
        /* Theme editor reloads re-render the section: drop any stale copy first */
        $$('body > #' + id).forEach((old) => old.remove());
        document.body.appendChild(drawer);
      }
    });

    /* Keep --header-height accurate (transparent header overlays the hero by this amount) */
    function measure() {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
    measure();

    function onScroll() {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      /* Hide on scroll down, reveal on scroll up (desktop & mobile) */
      if (y > 320 && y > lastScroll + 6 && !openPanel) header.classList.add('is-hidden');
      else if (y < lastScroll - 6 || y < 320) header.classList.remove('is-hidden');
      lastScroll = y;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Mega menu: open on hover with intent, keyboard accessible */
    $$('[data-mega-item]', header).forEach((item) => {
      const trigger = $('[data-mega-trigger]', item);
      let closeTimer;
      function open() {
        clearTimeout(closeTimer);
        $$('[data-mega-item].is-open', header).forEach((other) => {
          if (other !== item) other.classList.remove('is-open');
        });
        item.classList.add('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
      function close() {
        closeTimer = setTimeout(() => {
          item.classList.remove('is-open');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        }, 120);
      }
      item.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') open(); });
      item.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') close(); });
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          /* First tap/click on touch or keyboard opens; second follows link */
          if (!item.classList.contains('is-open')) {
            e.preventDefault();
            open();
          }
        });
      }
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) {
          item.classList.remove('is-open');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!header.contains(e.target)) {
        $$('[data-mega-item].is-open', header).forEach((item) => item.classList.remove('is-open'));
      }
    });

    /* Mobile menu submenu toggles */
    document.addEventListener('click', (e) => {
      const sub = e.target.closest('[data-submenu-toggle]');
      if (!sub) return;
      e.preventDefault();
      const li = sub.closest('li');
      const panel = li && li.querySelector('[data-submenu]');
      if (!panel) return;
      const isOpen = li.classList.toggle('is-open');
      sub.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  /* --- Predictive search --------------------------------------------------------------------------------- */

  function initPredictiveSearch() {
    const wrap = $('[data-predictive-search]');
    if (!wrap) return;
    const input = $('input[type="search"]', wrap);
    const results = $('[data-predictive-results]', wrap);
    if (!input || !results) return;
    let controller = null;

    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        results.hidden = true;
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) return;
        const html = await res.text();
        const dom = new DOMParser().parseFromString(html, 'text/html');
        const fresh = dom.querySelector('[data-predictive-output]');
        results.innerHTML = fresh ? fresh.innerHTML : '';
        results.hidden = !results.innerHTML.trim();
      } catch (err) {
        /* aborted or offline — leave previous results */
      }
    }, 260);

    input.addEventListener('input', run);
  }

  /* --- Announcement bar dismiss ---------------------------------------------------------------------- */

  function initAnnouncement() {
    const bar = $('[data-announcement]');
    if (!bar) return;
    const key = 'eliyeen:announcement:' + (bar.getAttribute('data-announcement-id') || 'default');
    if (sessionStorage.getItem(key) === 'dismissed') {
      bar.remove();
      return;
    }
    const closeBtn = $('[data-announcement-close]', bar);
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        bar.style.height = bar.offsetHeight + 'px';
        requestAnimationFrame(() => {
          bar.style.height = '0px';
          bar.style.opacity = '0';
        });
        setTimeout(() => bar.remove(), 350);
        sessionStorage.setItem(key, 'dismissed');
      });
    }
  }

  /* --- Cookie banner ------------------------------------------------------------------------------------ */

  function initCookieBanner() {
    const banner = $('[data-cookie-banner]');
    if (!banner) return;
    const KEY = 'eliyeen:cookie-consent';
    if (localStorage.getItem(KEY)) {
      banner.remove();
      return;
    }
    banner.removeAttribute('hidden');
    requestAnimationFrame(() => banner.classList.add('is-visible'));
    banner.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cookie-choice]');
      if (!btn) return;
      const choice = btn.getAttribute('data-cookie-choice');
      localStorage.setItem(KEY, choice);
      /* Inform Shopify's Customer Privacy API when available */
      if (window.Shopify && window.Shopify.customerPrivacy) {
        try {
          window.Shopify.customerPrivacy.setTrackingConsent(choice === 'accepted', () => {});
        } catch (err) { /* API not configured — consent stored locally only */ }
      }
      banner.classList.remove('is-visible');
      setTimeout(() => banner.remove(), 400);
    });
  }

  /* --- Newsletter feedback ------------------------------------------------------------------------------- */

  function initNewsletterForms() {
    /* Shopify redirects with ?customer_posted=true — surface success near the form */
    if (window.location.search.includes('customer_posted=true')) {
      const form = $('.newsletter-form');
      if (form) {
        const status = form.querySelector('[data-newsletter-status]');
        if (status) {
          status.hidden = false;
          status.focus && status.focus();
        }
        form.scrollIntoView({ block: 'center' });
      }
    }
  }

  /* --- Init ------------------------------------------------------------------------------------------------ */

  function initAll(root = document) {
    initCarousels(root);
    initSlideshows(root);
    initAccordions(root);
    initReveals(root);
    Wishlist.syncHearts();
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
    initHeader();
    initPredictiveSearch();
    initAnnouncement();
    initCookieBanner();
    initNewsletterForms();
    Cart.refreshCount();
    /* GSAP loads deferred; try now, then on load as fallback */
    initGsap();
    window.addEventListener('load', initGsap, { once: true });
  });

  /* Re-init inside sections when edited in the Theme Editor */
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  window.eliyeen.initAll = initAll;
})();
